/**
 * AlertsWidget — „Powiadomienia": komunikaty wyliczane na żywo (aktualizacje modułów, terminy
 * płatności i decyzji, błędy integracji) z ikoną wg ważności (info / ostrzeżenie / sukces / błąd)
 * i kropką „nieprzeczytane" wg Figmy „08.6". „Oznacz przeczytane" zapamiętuje identyfikatory
 * w przeglądarce (localStorage) — to wygoda per użytkownik/urządzenie, nie stan serwerowy.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Info, type LucideIcon, XCircle } from 'lucide-react';

import { Button } from '@/components/Button';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { cn } from '@/lib/utils';

import {
  type AlertRow,
  type AlertSeverity,
  classifyDataError,
  fetchAlerts,
} from '../dashboard-api';
import type { WidgetRenderProps } from '../types';
import { relativeTime } from '../widget-ui';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

const SEVERITY: Record<AlertSeverity, { icon: LucideIcon; tile: string }> = {
  info: {
    icon: Info,
    tile: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  warning: {
    icon: AlertTriangle,
    tile: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  success: {
    icon: CheckCircle2,
    tile: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  error: {
    icon: XCircle,
    tile: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
};

const STORAGE_KEY = 'saas-v2:dashboard-alerts-read';

function loadRead(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

function saveRead(ids: Set<string>) {
  try {
    // Ograniczamy rozmiar — stare identyfikatory i tak wypadają z listy (max 8 alertów).
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids].slice(-200)));
  } catch {
    // localStorage niedostępny (tryb prywatny) — stan przeczytania żyje tylko do odświeżenia.
  }
}

export function AlertsWidget({ widget }: WidgetRenderProps) {
  const { t } = useTranslation();
  const { isAll, mode, companyId } = useActiveCompanyScope();
  const blocked = mode === 'none';
  const [read, setRead] = useState<Set<string>>(loadRead);

  const query = useQuery({
    queryKey: ['tenant', 'dashboard', 'alerts', companyId ?? 'all'],
    queryFn: () => fetchAlerts(isAll ? 'all' : 'company'),
    enabled: widget.type === 'alerts' && !blocked,
    staleTime: 30_000,
    retry: false,
  });

  const markAllRead = useCallback(
    (rows: AlertRow[]) => {
      const next = new Set(read);
      for (const r of rows) next.add(r.id);
      setRead(next);
      saveRead(next);
    },
    [read],
  );

  if (widget.type !== 'alerts') return null;

  if (blocked) return <WidgetEmpty message={t('tenant.dashboard.pickCompany')} />;
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );

  const rows = query.data ?? [];
  if (rows.length === 0)
    return <WidgetEmpty message={t('tenant.dashboard.widgets.alerts.empty')} />;

  const hasUnread = rows.some((r) => r.unread && !read.has(r.id));

  return (
    <div className="flex h-full flex-col gap-2">
      {hasUnread && (
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => markAllRead(rows)}
          onMouseDown={(e) => e.stopPropagation()}
          className="h-7 self-end px-2 text-xs"
        >
          {t('tenant.dashboard.widgets.alerts.markRead')}
        </Button>
      )}
      <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto">
        {rows.map((row) => {
          const { icon: Icon, tile } = SEVERITY[row.severity];
          const unread = row.unread && !read.has(row.id);
          const body = (
            <>
              <span
                aria-hidden="true"
                className={cn(
                  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                  tile,
                )}
              >
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className={cn(
                      'truncate text-[13px] text-text-primary',
                      unread ? 'font-semibold' : 'font-medium',
                    )}
                    title={row.title}
                  >
                    {row.title}
                  </span>
                  <span className="shrink-0 text-[11px] text-text-muted">
                    {relativeTime(row.at)}
                  </span>
                </div>
                <p className="line-clamp-2 text-xs text-text-tertiary">{row.description}</p>
              </div>
              {unread && (
                <span
                  className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent-500"
                  role="img"
                  aria-label={t('tenant.dashboard.widgets.alerts.unread')}
                />
              )}
            </>
          );
          const base = 'flex items-start gap-2.5 rounded-lg px-2 py-2 hover:bg-bg-secondary';
          return (
            <li key={row.id}>
              {row.href ? (
                <Link to={row.href} onMouseDown={(e) => e.stopPropagation()} className={base}>
                  {body}
                </Link>
              ) : (
                <div className={base}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
